/**
 * whiteboardCaptureQuality
 *
 * Pre-flight quality check for a whiteboard photo, run in the browser before a
 * frame is sent for analysis. People "think out loud" on big walls, so the most
 * common failure isn't recognition — it's a capture that's too dark, too far
 * away, or too blurry for the handwriting to survive the vision model's
 * downscale (~1568 px long edge). This module turns a frame into actionable
 * guidance: "add light", "move closer", "hold steady".
 *
 * The math is pure and deterministic so it can be unit-tested. The caller is
 * responsible for downsampling the source photo onto a small working canvas
 * (≈512 px long edge) and passing the resulting pixels plus the *original*
 * photo's long edge (which the resolution check needs).
 */

export interface ImagePixels {
  /** RGBA bytes, length = width * height * 4 (e.g. ImageData.data). */
  data: Uint8ClampedArray | number[];
  width: number;
  height: number;
}

export type CaptureQualityLevel = 'good' | 'warn' | 'poor';

export interface CaptureQuality {
  level: CaptureQualityLevel;
  /** Mean luminance (0–255) of the working image. */
  meanLuminance: number;
  /** Laplacian variance — higher means sharper / more high-frequency detail. */
  sharpness: number;
  /** Long edge of the ORIGINAL photo, in pixels. */
  longEdgePx: number;
  /** Human-readable guidance, most important first. */
  warnings: string[];
  flags: {
    tooDark: boolean;
    tooBlurryOrFar: boolean;
    lowResolution: boolean;
  };
}

// ── Thresholds ──────────────────────────────────────────────────────────────
// Tuned for a ~512 px-long-edge working image. A well-lit whiteboard is mostly
// white, so a correctly exposed frame reads bright; sharpness is scale-stabilized
// by the fixed working size.

/** Below this mean luminance the frame is underlit. */
export const MIN_MEAN_LUMINANCE = 70;
/** Below this the source has too few pixels to fill the model's ~1568px window. */
export const MIN_LONG_EDGE_PX = 1400;
/** Below this Laplacian variance the frame is almost certainly blurry or too far. */
export const SHARPNESS_POOR = 80;
/** Below this it's borderline soft. */
export const SHARPNESS_WARN = 200;

function meanLuminanceAndGray(p: ImagePixels): { mean: number; gray: Float64Array } {
  const { data, width, height } = p;
  const n = width * height;
  const gray = new Float64Array(n);
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const r = data[i * 4];
    const g = data[i * 4 + 1];
    const b = data[i * 4 + 2];
    const y = 0.299 * r + 0.587 * g + 0.114 * b;
    gray[i] = y;
    sum += y;
  }
  return { mean: n > 0 ? sum / n : 0, gray };
}

/**
 * Variance of the discrete Laplacian — a standard, cheap focus measure. A flat
 * or out-of-focus image has near-zero high-frequency energy; crisp text edges
 * produce a large variance. Border pixels are skipped.
 */
function laplacianVariance(gray: Float64Array, w: number, h: number): number {
  if (w < 3 || h < 3) return 0;
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap = 4 * gray[i] - gray[i - 1] - gray[i + 1] - gray[i - w] - gray[i + w];
      sum += lap;
      sumSq += lap * lap;
      count++;
    }
  }
  if (count === 0) return 0;
  const mean = sum / count;
  return Math.max(0, sumSq / count - mean * mean);
}

/**
 * Assess a downsampled working frame and the original photo's long edge.
 * Pure & deterministic.
 */
export function assessImageQuality(working: ImagePixels, originalLongEdgePx: number): CaptureQuality {
  const { mean, gray } = meanLuminanceAndGray(working);
  const sharpness = laplacianVariance(gray, working.width, working.height);

  const tooDark = mean < MIN_MEAN_LUMINANCE;
  const lowResolution = originalLongEdgePx < MIN_LONG_EDGE_PX;
  const tooBlurryOrFar = sharpness < SHARPNESS_POOR;

  const warnings: string[] = [];
  if (tooDark) {
    warnings.push('Looks underlit — add light or move to a brighter spot so the writing is readable.');
  }
  if (lowResolution) {
    warnings.push('Low resolution — use a larger or closer photo so small writing survives.');
  }
  if (tooBlurryOrFar) {
    warnings.push('Looks blurry or too far away — move closer, hold steady, and let it focus.');
  } else if (sharpness < SHARPNESS_WARN) {
    warnings.push('A little soft — moving closer or steadying the shot will read more reliably.');
  }

  let level: CaptureQualityLevel = 'good';
  if (tooDark || lowResolution || tooBlurryOrFar) {
    level = 'poor';
  } else if (sharpness < SHARPNESS_WARN) {
    level = 'warn';
  }

  return {
    level,
    meanLuminance: mean,
    sharpness,
    longEdgePx: originalLongEdgePx,
    warnings,
    flags: { tooDark, tooBlurryOrFar, lowResolution },
  };
}

/**
 * Estimate how many overlapping photos a wall needs to keep handwriting legible,
 * given the wall's width. Used to guide multi-photo "walk the wall" capture.
 * One frame reliably reads ~2.4 m of normal (~1.5") writing; with ~30% overlap
 * each added frame covers ~1.7 m of new wall.
 */
export const SINGLE_FRAME_LEGIBLE_WIDTH_M = 2.4;

export function recommendedPhotoCount(wallWidthMeters: number): number {
  if (!Number.isFinite(wallWidthMeters) || wallWidthMeters <= 0) return 1;
  if (wallWidthMeters <= SINGLE_FRAME_LEGIBLE_WIDTH_M) return 1;
  const effectivePerExtraFrame = SINGLE_FRAME_LEGIBLE_WIDTH_M * 0.7; // 30% overlap
  return 1 + Math.ceil((wallWidthMeters - SINGLE_FRAME_LEGIBLE_WIDTH_M) / effectivePerExtraFrame);
}
